// src/modules/assetManagement/management/useUploadQueue.ts
// Simulated upload pipeline: validate → upload → process → create asset. Every failed attempt is logged.
import { useCallback, useEffect, useRef, useState } from "react";
import { assetApi } from "../assetApi";
import type { AssetKind, UploadStage } from "../types";
import { ACCEPTED_EXTENSIONS, extensionOf, formatBytes, MAX_ATTEMPTS, MAX_FILE_BYTES } from "../utils";

export type QueueStatus = "invalid" | "ready" | "uploading" | "processing" | "done" | "failed";

export interface QueueItem {
  key: string;
  file: File;
  kind: AssetKind;
  status: QueueStatus;
  progress: number;
  attempts: number;
  stage?: UploadStage;
  errorCode?: string;
  error?: string;
  assetId?: string;
  ticketRef?: string;
  /** progress % at which this attempt will fail, if it will */
  failAt?: number | null;
  failStage?: UploadStage | null;
  processingUntil?: number;
  options?: UploadOptions;
}

export interface UploadOptions {
  tags: string[];
  eventIds: string[];
  productIds: string[];
}

const CONCURRENCY = 3;
/** Marks an item as waiting for the ticker to plan its next attempt */
const planPending = { failAt: undefined, failStage: undefined } as const;
const TICK_MS = 250;

export const fileKeyOf = (f: File) => `${f.name}-${f.size}-${f.lastModified}`;

const validate = (file: File): { code: string; message: string } | null => {
  const ext = extensionOf(file.name);
  if (!ACCEPTED_EXTENSIONS[ext]) {
    return { code: "UNSUPPORTED_FORMAT", message: `.${ext || "unknown"} files are not on the supported formats list` };
  }
  if (file.size === 0) return { code: "EMPTY_FILE", message: "The file is empty (0 bytes)" };
  if (file.size > MAX_FILE_BYTES) {
    return { code: "FILE_TOO_LARGE", message: `File is ${formatBytes(file.size)}; the limit is 10 GB per file` };
  }
  return null;
};

/** Decide up front whether this attempt fails, and where (mock only) */
const planAttempt = (file: File, simulateFailures: boolean): Pick<QueueItem, "failAt" | "failStage"> => {
  const name = file.name.toLowerCase();
  if (name.includes("corrupt")) return { failAt: 100, failStage: "processing" };
  if (name.includes("fail") || simulateFailures) {
    return { failAt: 20 + Math.round(Math.random() * 60), failStage: "upload" };
  }
  return { failAt: null, failStage: null };
};

const UPLOAD_ERRORS = [
  { code: "NETWORK_TIMEOUT", message: (p: number) => `Connection timed out at ${p}%` },
  { code: "CHECKSUM_MISMATCH", message: () => "Checksum of the received file did not match" },
  { code: "STORAGE_UNAVAILABLE", message: () => "Storage service did not respond (503)" },
];

interface Effect {
  type: "log" | "create";
  item: QueueItem;
}

export function useUploadQueue() {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [simulateFailures, setSimulateFailures] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const logFailure = (item: QueueItem) =>
    assetApi.logUploadFailure({
      fileKey: item.key,
      fileName: item.file.name,
      sizeBytes: item.file.size,
      mimeType: item.file.type || "application/octet-stream",
      attempt: Math.max(item.attempts, 1),
      stage: item.stage ?? "upload",
      errorCode: item.errorCode ?? "UNKNOWN",
      errorMessage: item.error ?? "Unknown error",
    });

  const addFiles = useCallback((files: FileList | File[]) => {
    const existing = new Set(itemsRef.current.map((i) => i.key));
    const added: QueueItem[] = [];
    Array.from(files).forEach((file) => {
      const key = fileKeyOf(file);
      if (existing.has(key)) return;
      existing.add(key);
      const problem = validate(file);
      const item: QueueItem = {
        key,
        file,
        kind: ACCEPTED_EXTENSIONS[extensionOf(file.name)] ?? "document",
        status: problem ? "invalid" : "ready",
        progress: 0,
        attempts: problem ? 1 : 0,
        stage: problem ? "validation" : undefined,
        errorCode: problem?.code,
        error: problem?.message,
      };
      if (problem) void logFailure(item);
      added.push(item);
    });
    setItems((prev) => [...prev, ...added]);
  }, []);

  const start = useCallback((options: UploadOptions) => {
    setItems((prev) => prev.map((i) => (i.status === "ready" ? { ...i, status: "uploading", progress: 0, options, ...planPending } : i)));
  }, []);

  const retry = useCallback((key: string) => {
    setItems((prev) =>
      prev.map((i) =>
        i.key === key && i.status === "failed" && i.attempts < MAX_ATTEMPTS
          ? { ...i, status: "uploading", progress: 0, error: undefined, errorCode: undefined, stage: undefined, ...planPending }
          : i,
      ),
    );
  }, []);

  const remove = useCallback((key: string) => setItems((prev) => prev.filter((i) => i.key !== key)), []);
  const clearFinished = useCallback(
    () => setItems((prev) => prev.filter((i) => i.status !== "done" && i.status !== "invalid")),
    [],
  );
  const markTicket = useCallback(
    (key: string, ticketRef: string) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ticketRef } : i))),
    [],
  );

  const active = items.some((i) => i.status === "uploading" || i.status === "processing");

  // Single ticker advances every active upload; side effects run outside the state update
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => {
      const effects: Effect[] = [];
      let running = itemsRef.current.filter((i) => i.status === "processing" || (i.status === "uploading" && i.failAt !== undefined)).length;

      const next = itemsRef.current.map((item): QueueItem => {
        // Plan new attempts lazily so concurrency stays at CONCURRENCY
        if (item.status === "uploading" && item.failAt === undefined) {
          if (running >= CONCURRENCY) return item;
          running += 1;
          return { ...item, attempts: item.attempts + 1, ...planAttempt(item.file, simulateFailures) };
        }
        if (item.status === "uploading") {
          const progress = Math.min(100, item.progress + 3 + Math.random() * 9);
          if (item.failAt != null && item.failStage === "upload" && progress >= item.failAt) {
            const err = UPLOAD_ERRORS[Math.floor(Math.random() * UPLOAD_ERRORS.length)];
            const failed: QueueItem = {
              ...item,
              status: "failed",
              progress: Math.round(progress),
              stage: "upload",
              errorCode: err.code,
              error: err.message(Math.round(progress)),
            };
            effects.push({ type: "log", item: failed });
            return failed;
          }
          if (progress >= 100) return { ...item, progress: 100, status: "processing", processingUntil: Date.now() + 1400 };
          return { ...item, progress };
        }
        if (item.status === "processing" && Date.now() >= (item.processingUntil ?? 0)) {
          if (item.failStage === "processing") {
            const failed: QueueItem = {
              ...item,
              status: "failed",
              stage: "processing",
              errorCode: "TRANSCODE_FAILED",
              error: "The file could not be decoded; it may be corrupt",
            };
            effects.push({ type: "log", item: failed });
            return failed;
          }
          const done: QueueItem = { ...item, status: "done" };
          effects.push({ type: "create", item: done });
          return done;
        }
        return item;
      });

      itemsRef.current = next;
      setItems(next);

      effects.forEach(async ({ type, item }) => {
        if (type === "log") {
          void logFailure(item);
          return;
        }
        const f = item.file;
        const previewable = item.kind === "image" || item.kind === "video" || item.kind === "audio";
        const asset = await assetApi.createAsset({
          filename: f.name,
          kind: item.kind,
          mimeType: f.type || "application/octet-stream",
          sizeBytes: f.size,
          status: "completed",
          storage: "standard",
          restoreReadyAt: null,
          tags: item.options?.tags ?? [],
          eventIds: item.options?.eventIds ?? [],
          productIds: item.options?.productIds ?? [],
          previewUrl: previewable ? URL.createObjectURL(f) : null,
          errorMessage: null,
        });
        if (item.attempts > 1) void assetApi.resolveFileAttempts(item.key, "retried_ok");
        setItems((prev) => prev.map((i) => (i.key === item.key ? { ...i, assetId: asset.id } : i)));
      });
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [active, simulateFailures]);

  return { items, addFiles, start, retry, remove, clearFinished, markTicket, simulateFailures, setSimulateFailures };
}

